import AtmosphericHero from '@/components/marketing/AtmosphericHero'
import ScrollReveal from '@/components/marketing/ScrollReveal'
import FeatureFaq from '@/components/marketing/features/FeatureFaq'
import Showcase, { INTERACTIVE_FRAME_HEIGHT, isInteractive, type InteractiveScreen } from '@/components/marketing/features/showcase/Showcase'
import type { FeaturesStandoutProps } from '@/types/features'

// What the page says around a demo that can be used (INTERACTIVE_SCREENS):
// how to try it, and — outside the frame — that nothing in it is kept.
// A Record over every interactive screen, so a new one without its copy fails tsc
// instead of silently drawing no demo.
const DEMO_COPY: Record<InteractiveScreen, { lead: string; note: string; label: string }> = {
  'live-chat-full': {
    lead: 'This is the real chat from the dashboard, over the release builder. Type a message and press Enter.',
    note: 'Demo — messages are not sent',
    label: 'Live chat demo: the real wavbee support chat over the release builder, with sample data. Messages are not sent.',
  },
  'lyrics-full': {
    lead: 'This is the real lyrics studio from the dashboard, open on a track with sample lyrics. Switch between Write, Sync and Preview, pick a line and move its time, or edit the words.',
    note: 'Demo — changes are not saved',
    label: 'Lyrics studio demo: the real wavbee lyrics studio over the release builder, with sample lyrics. Changes are not saved.',
  },
}

export default function StandoutPage(props: FeaturesStandoutProps) {
  // A demo you can use needs room to be read and typed in: full width, under the hero.
  const copy = isInteractive(props.screen) ? DEMO_COPY[props.screen] : null
  return (
    <>
      <AtmosphericHero minHeight="48vh">
        <div className="mx-auto max-w-6xl px-6">
          <a href={props.back.href} className="marketing-inline-link inline-block text-sm font-semibold">
            <span aria-hidden="true">← </span>{props.back.label}
          </a>
          <div className="mt-8 grid max-w-3xl items-center gap-10">
            <div className="space-y-8">
              <span className="hero-eyebrow">{props.hero.eyebrow}</span>
              <h1 className="hero-headline" style={{ fontSize: 'clamp(2.25rem, 5vw, 4rem)' }}>{props.hero.title}</h1>
              <p className="hero-body">{props.hero.body}</p>
            </div>
          </div>
        </div>
      </AtmosphericHero>

      {copy && (
        <section className="marketing-section" aria-labelledby="feature-demo-title">
          <div className="container mx-auto px-6">
            <div className="mx-auto max-w-6xl">
              <h2 id="feature-demo-title" className="marketing-section-title">Try it</h2>
              <p className="marketing-lead mt-3 max-w-3xl">
                {copy.lead}
              </p>
              <p className="marketing-chip mt-3 inline-block">
                {copy.note}
              </p>
            </div>
            <Showcase screen={props.screen} variant="artist" height={INTERACTIVE_FRAME_HEIGHT} className="mt-8"
              label={copy.label} />
          </div>
        </section>
      )}

      <section className="marketing-section">
        <div className="container mx-auto px-6">
          <ScrollReveal direction="up">
            <div className="grid gap-6 md:grid-cols-2">
              {props.sections.map((s) => (
                <div key={s.title} className="marketing-surface-card">
                  <h2 className="text-xl font-semibold text-white">{s.title}</h2>
                  <p className="mt-3 text-sm leading-7 text-white/72">{s.body}</p>
                </div>
              ))}
            </div>
          </ScrollReveal>
        </div>
      </section>

      <FeatureFaq faq={props.faq} />

      <section className="marketing-section">
        <div className="container mx-auto px-6">
          <ScrollReveal direction="up">
            <div className="marketing-final-cta">
              <div>
                <h2 className="marketing-section-title">{props.cta.title}</h2>
                <p className="marketing-lead max-w-2xl">{props.cta.body}</p>
              </div>
              <div className="mt-8 flex flex-wrap gap-3">
                {props.cta.actions.map((action, i) => (
                  <a key={action.href} href={action.href} className={i === 0 ? 'marketing-button-primary' : 'marketing-button-secondary'}>
                    {action.label}
                  </a>
                ))}
              </div>
            </div>
          </ScrollReveal>
        </div>
      </section>
    </>
  )
}
